import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import {
  BulkAssignFlexiAdvisorsRequest,
  FlexiAdvisorAssignment,
  FlexiAdvisorAssignmentsPage,
  FlexiAdvisorCoverage,
  FlexiBusinessAdvisor,
} from './flexi-advisor-settings.models';

/**
 * HSE advisors per Flexi business — FlexiAdvisorsController.
 *
 * The list, save and bulk endpoints are Admin only and scoped by the backend
 * to the caller's own application. The per-business read is open to anyone who
 * may open that business, for the read-only list in business settings.
 */
@Injectable({ providedIn: 'root' })
export class FlexiAdvisorSettingsService {
  private readonly url = `${environment.BASE_URL_V2}/flexi-advisors`;

  constructor(private http: HttpClient) {}

  /** Loads one filtered page; workload totals always cover the whole application. */
  assignments(
    pageNumber: number,
    pageSize: number,
    search: string,
    coverage: FlexiAdvisorCoverage | null,
    myBusinesses = false,
    primary: boolean | null = null,
  ): Observable<FlexiAdvisorAssignmentsPage> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('search', search)
      .set('scope', myBusinesses ? 'mine' : 'all');
    if (coverage) {
      params = params.set('coverage', coverage);
    }
    if (primary !== null) {
      params = params.set('primary', primary);
    }
    return this.http.get<FlexiAdvisorAssignmentsPage>(`${this.url}/assignments`, {
      params,
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  /** Save failures are shown inside the dialog, so the global toast is suppressed. */
  setAdvisors(businessId: number, userIds: number[]): Observable<FlexiAdvisorAssignment> {
    return this.http.put<FlexiAdvisorAssignment>(
      `${this.url}/businesses/${businessId}`,
      { UserIds: userIds },
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) },
    );
  }

  /** Save failures are shown inside the dialog, so the global toast is suppressed. */
  bulkAssign(request: BulkAssignFlexiAdvisorsRequest): Observable<FlexiAdvisorAssignment[]> {
    return this.http.post<FlexiAdvisorAssignment[]>(`${this.url}/bulk`, request, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  /** A failure is shown beside the read-only list, so the global toast is suppressed. */
  businessAdvisors(businessId: number): Observable<FlexiBusinessAdvisor[]> {
    return this.http.get<FlexiBusinessAdvisor[]>(`${this.url}/businesses/${businessId}`, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }
}
